import { Box } from '@mui/material'
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridRenderCellParams,
  GridSortModel,
  getGridStringOperators,
  useGridApiRef,
} from '@mui/x-data-grid-premium'
import { useContact } from 'api/aws/useContact'
import { useUserItems } from 'api/aws/users/useUserItems'
import { useEntitiesFromSearch } from 'api/search'
import GlobalDataGrid from 'components/DataGrid/GlobalDataGrid'
import { customNotContainsOperator } from 'components/DataGrid/operators/customNotContainsOperator'
import { customSortOperator } from 'components/DataGrid/operators/customSortOperator'
import { getDensityStyles } from 'components/DataGrid/operators/getDensityStyles'
import DateTimeCell from 'components/Organizations/grid/cells/DateTimeCell'
import OrganizationStatusCell from 'components/Organizations/grid/cells/OrganizationStatusCell'
import OwnerCell from 'components/Organizations/grid/cells/OwnerCell'
import TypeCell from 'components/Organizations/grid/cells/TypeCell'
import VerticalCell from 'components/Organizations/grid/cells/VerticalCell'
import RenderOrganizationCell from 'components/MTB/grid/cells/RenderOrganizationCell'
import { useTreeExpansionPersistence } from 'components/DataGrid/useTreeExpansionPersistence'
import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

const defaultSortModel: GridSortModel = [{ field: 'name', sort: 'asc' }]
const defaultFilterModel: GridFilterModel = { items: [], quickFilterValues: [] }

const initialVisibilityState: GridColumnVisibilityModel = {
  org_uuid: false,
  __tree_data_group__: false,
}

const ContactOrgsInfo = () => {
  const { contactId } = useParams()
  const apiRef = useGridApiRef()
  const navigate = useNavigate()
  const { isExpanded, toggleExpanded } = useTreeExpansionPersistence('contact_orgs')

  const { data: contact } = useContact(contactId)
  const { organizations } = useEntitiesFromSearch()
  const { data: users } = useUserItems()

  const contactOrgs = useMemo(() => {
    if (!contact?.organizations?.length || !Array.isArray(organizations)) return []
    return organizations
      .filter(o => contact.organizations.includes(o?.org_uuid))
      .map(org => {
        const ownerId = Array.isArray(org?.owner) ? org.owner[0] : org?.owner ?? null
        const owner = ownerId ? (users ?? []).find(u => u?.user_uuid === ownerId) : null
        return {
          ...org,
          owner,
          isParent: true,
          id: org?.org_uuid ?? org?.PK,
          rowId: org?.PK ?? `ORG_${org?.org_uuid}`,
        }
      })
  }, [contact?.organizations, organizations, users])

  const densityStyles = getDensityStyles('standard')

  const handleRowClick = (row: GridRenderCellParams['row']) => {
    if (row?.PK) navigate(`/organizations/${row.PK}/details`)
  }

  const columnMap: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Organization Name',
      aggregable: false,
      width: 250,
      headerAlign: 'left',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      valueGetter: (_, row) => row?.name,
      renderCell: (params: GridRenderCellParams) => (
        <RenderOrganizationCell
          params={params}
          densityStyles={densityStyles}
          handleRowClick={handleRowClick}
          toggleExpanded={toggleExpanded}
        />
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      aggregable: false,
      width: 150,
      headerAlign: 'left',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      valueGetter: (_, row) => row?.type ?? '',
      renderCell: (params: GridRenderCellParams) => <TypeCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'status',
      headerName: 'Status',
      aggregable: false,
      width: 100,
      headerAlign: 'left',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      valueGetter: (_, row) => row?.status ?? '',
      renderCell: (params: GridRenderCellParams) => (
        <OrganizationStatusCell params={params} densityStyles={densityStyles} />
      ),
    },
    {
      field: 'vertical',
      headerName: 'Vertical',
      width: 100,
      headerAlign: 'center',
      sortable: false,
      valueGetter: (_, row) => row?.vertical,
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      renderCell: (params: GridRenderCellParams) => <VerticalCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'owner',
      headerName: 'Owner',
      width: 50,
      headerAlign: 'center',
      valueGetter: (_, row) => row?.owner?.name ?? '',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      renderCell: (params: GridRenderCellParams) => <OwnerCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'creation_timestamp',
      headerName: 'Created',
      width: 75,
      headerAlign: 'center',
      valueGetter: (_value, row) => row?.creation_timestamp ?? null,
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      renderCell: (params: GridRenderCellParams) => <DateTimeCell params={params} densityStyles={densityStyles} />,
    },
  ]

  if (!contactOrgs.length) {
    return (
      <Box sx={{ p: 2, color: 'text.secondary' }}>No organizations assigned to this contact.</Box>
    )
  }

  return (
    <GlobalDataGrid
      rows={contactOrgs}
      columns={columnMap}
      apiRef={apiRef}
      context={'contact_orgs'}
      isExpanded={isExpanded}
      defaultFilterModel={defaultFilterModel}
      defaultVisibilityModel={initialVisibilityState}
      defaultSortModel={defaultSortModel}
      defaultGridDensity={'standard'}
      checkboxSelection={false}
      gridContainerSx={{
        position: 'relative',
        display: 'flex',
        maxHeight: '100vh',
        width: '100%',
        justifyContent: 'center',
        transition: 'all 0.3s ease-in-out',
        height: '100%',
      }}
      treeData={false}
    />
  )
}

export default ContactOrgsInfo
